
















































































































/* eslint-disable max-lines-per-function */
import { computed, defineComponent, PropType, ref, watch } from '@nuxtjs/composition-api';
import { BaseTab, BaseTabs, UCSkeletonShimmer } from '@shared/ui';

import {
  IFacetOptionsBySection,
  IPreFilterOption,
  ISearchPreFiltersConfig,
  ISearchPreFiltersValue,
} from './types';

export default defineComponent({
  name: 'SearchPreFiltersContent',
  components: {
    BaseTabs,
    BaseTab,
    UCSkeletonShimmer,
  },
  props: {
    config: {
      type: Object as PropType<ISearchPreFiltersConfig>,
      required: true,
    },
    value: {
      type: Object as PropType<ISearchPreFiltersValue>,
      default: () => ({}),
    },
    facetOptions: {
      type: Object as PropType<IFacetOptionsBySection>,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const activeTabIndex = ref(1); // BaseTabs is 1-indexed
    const localValue = ref<ISearchPreFiltersValue>({ ...props.value });
    const localPriceMin = ref<number | null>(props.value.priceMin ?? null);
    const localPriceMax = ref<number | null>(props.value.priceMax ?? null);

    const hasSelections = computed(() => {
      const hasFacetSelections = Object.values(localValue.value).some(
        (v) => Array.isArray(v) && v.length > 0,
      );
      const hasPriceSelections = localPriceMin.value !== null || localPriceMax.value !== null;
      return hasFacetSelections || hasPriceSelections;
    });

    // Mirror the committed context into local editing state. When the consumer clears it
    // (e.g. after Search), value becomes {} and the modal empties. Do NOT reset activeTabIndex
    // here: changing it fires BaseTabs' openTab watcher → click:tab → a re-commit that would
    // resurrect the context we just cleared. (See REQUIREMENTS.md.)
    watch(
      () => props.value,
      (newValue) => {
        localValue.value = { ...newValue };
        localPriceMin.value = newValue.priceMin ?? null;
        localPriceMax.value = newValue.priceMax ?? null;
      },
      { deep: true },
    );

    const optionsFor = (sectionName: string): IPreFilterOption[] =>
      props.facetOptions[sectionName] ?? [];

    const selectionFor = (sectionName: string): string[] => {
      const current = localValue.value[sectionName];
      return Array.isArray(current) ? current : [];
    };

    // activeTabIndex is 1-based (BaseTabs); consumers get a 0-based index.
    function emitChange() {
      emit('change', localValue.value, activeTabIndex.value - 1);
    }

    function onTabClick(tabIndex: number) {
      activeTabIndex.value = tabIndex;
      emitChange();
    }

    function isOptionChecked(sectionName: string, value: string): boolean {
      return selectionFor(sectionName).includes(value);
    }

    function isAllChecked(sectionName: string): boolean {
      const available = optionsFor(sectionName).map((o) => o.value);
      const selected = selectionFor(sectionName);
      return available.length > 0 && available.every((v) => selected.includes(v));
    }

    function onCheckboxChange(sectionName: string, value: string) {
      const current = selectionFor(sectionName);
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      localValue.value = { ...localValue.value, [sectionName]: next };
      emitChange();
    }

    function onAllChange(sectionName: string) {
      const available = optionsFor(sectionName).map((o) => o.value);
      const current = selectionFor(sectionName);
      const allSelected = available.length > 0 && available.every((v) => current.includes(v));
      localValue.value = {
        ...localValue.value,
        [sectionName]: allSelected ? [] : available,
      };
      emitChange();
    }

    function onPriceChange() {
      localValue.value = {
        ...localValue.value,
        priceMin: localPriceMin.value,
        priceMax: localPriceMax.value,
      };
      emitChange();
    }

    function onClearFilters() {
      localValue.value = {};
      localPriceMin.value = null;
      localPriceMax.value = null;
      // Only `clear` — it resets the committed context. Emitting `change` here would
      // immediately re-commit the (now empty) active tab and defeat the reset.
      emit('clear');
    }

    // Emit ONLY `search`. The consumer commits this payload itself, then clears the context;
    // a trailing `change` here would re-commit after that clear and resurrect the badge.
    function onSearch() {
      emit('search', localValue.value, activeTabIndex.value - 1);
    }

    return {
      activeTabIndex,
      localPriceMin,
      localPriceMax,
      hasSelections,
      optionsFor,
      onTabClick,
      isOptionChecked,
      isAllChecked,
      onCheckboxChange,
      onAllChange,
      onPriceChange,
      onClearFilters,
      onSearch,
    };
  },
});
